/**
 * All available permission keys in the system.
 * Format: <module>.<action> or <module>.<field>.<action>
 */
export const PERMISSION_KEYS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard.view',

  // Orders
  ORDERS_VIEW: 'orders.view',
  ORDERS_CREATE: 'orders.create',
  ORDERS_EDIT: 'orders.edit',
  ORDERS_DELETE: 'orders.delete',
  ORDERS_APPROVE: 'orders.approve',
  ORDERS_EXPORT: 'orders.export',
  ORDERS_PRICE_VIEW: 'orders.price.view',
  ORDERS_PRICE_EDIT: 'orders.price.edit',
  ORDERS_STATUS_EDIT: 'orders.status.edit',
  ORDERS_REOPEN_CLOSED: 'orders.reopen-closed',
  ORDERS_ICOUNT: 'orders.icount',

  // Sub-Projects
  SUBPROJECTS_VIEW: 'subprojects.view',
  SUBPROJECTS_CREATE: 'subprojects.create',
  SUBPROJECTS_EDIT: 'subprojects.edit',
  SUBPROJECTS_DELETE: 'subprojects.delete',
  SUBPROJECTS_EXPORT: 'subprojects.export',
  SUBPROJECTS_PRICE_VIEW: 'subprojects.price.view',
  SUBPROJECTS_PRICE_EDIT: 'subprojects.price.edit',

  // Projects
  PROJECTS_VIEW: 'projects.view',
  PROJECTS_CREATE: 'projects.create',
  PROJECTS_EDIT: 'projects.edit',
  PROJECTS_DELETE: 'projects.delete',
  PROJECTS_EXPORT: 'projects.export',

  // Inspections
  INSPECTIONS_VIEW: 'inspections.view',
  INSPECTIONS_CREATE: 'inspections.create',
  INSPECTIONS_EDIT: 'inspections.edit',
  INSPECTIONS_DELETE: 'inspections.delete',
  INSPECTIONS_MANAGE: 'inspections.manage',
  INSPECTIONS_APPROVE: 'inspections.approve',
  INSPECTIONS_EXPORT: 'inspections.export',
  INSPECTIONS_SKETCH: 'inspections.sketch',
  INSPECTIONS_REOPEN: 'inspections.reopen',

  // Inspection Templates
  TEMPLATES_VIEW: 'templates.view',
  TEMPLATES_CREATE: 'templates.create',
  TEMPLATES_EDIT: 'templates.edit',
  TEMPLATES_DELETE: 'templates.delete',

  // Inspections Bank
  INSPECTIONS_BANK_VIEW: 'inspections-bank.view',
  INSPECTIONS_BANK_EDIT: 'inspections-bank.edit',

  // Scheduling Settings (inspection-duration buckets + default)
  SCHEDULING_SETTINGS_VIEW: 'scheduling-settings.view',
  SCHEDULING_SETTINGS_EDIT: 'scheduling-settings.edit',

  // AI settings (provider/model, thresholds) + AI mail triage review
  AI_SETTINGS_VIEW: 'ai-settings.view',
  AI_SETTINGS_EDIT: 'ai-settings.edit',
  MAIL_TRIAGE_VIEW: 'mail-triage.view',
  MAIL_TRIAGE_MANAGE: 'mail-triage.manage',
  /** AI costs and usage (Super-admin > Usage), for finance. */
  AI_USAGE_VIEW: 'ai-usage.view',
  /** Connect a chat client (Claude, ChatGPT) to LCL data through the MCP server. */
  MCP_CONNECT: 'mcp.connect',
  /** Server time, storage and versions (Super-admin > System). */
  SYSTEM_VIEW: 'system.view',

  // Companies
  COMPANIES_VIEW: 'companies.view',
  COMPANIES_CREATE: 'companies.create',
  COMPANIES_EDIT: 'companies.edit',
  COMPANIES_DELETE: 'companies.delete',
  COMPANIES_EXPORT: 'companies.export',
  COMPANIES_ADMIN: 'companies.admin',
  COMPANIES_STATUS_EDIT: 'companies.status.edit',

  // Contacts
  CONTACTS_VIEW: 'contacts.view',
  CONTACTS_CREATE: 'contacts.create',
  CONTACTS_EDIT: 'contacts.edit',
  CONTACTS_DELETE: 'contacts.delete',
  CONTACTS_EXPORT: 'contacts.export',

  // Contractors
  CONTRACTORS_VIEW: 'contractors.view',
  CONTRACTORS_CREATE: 'contractors.create',
  CONTRACTORS_EDIT: 'contractors.edit',
  CONTRACTORS_DELETE: 'contractors.delete',
  CONTRACTORS_EXPORT: 'contractors.export',

  // Planners
  PLANNERS_VIEW: 'planners.view',
  PLANNERS_CREATE: 'planners.create',
  PLANNERS_EDIT: 'planners.edit',
  PLANNERS_DELETE: 'planners.delete',
  PLANNERS_EXPORT: 'planners.export',

  // Users
  USERS_VIEW: 'users.view',
  USERS_CREATE: 'users.create',
  USERS_EDIT: 'users.edit',
  USERS_DELETE: 'users.delete',
  USERS_EXPORT: 'users.export',
  USERS_MANAGE: 'users.manage',

  // Certificates
  CERTIFICATES_VIEW: 'certificates.view',
  CERTIFICATES_EDIT: 'certificates.edit',
  CERTIFICATES_ADMIN: 'certificates.admin',
  CERTIFICATES_UPLOAD: 'certificates.upload',
  CERTIFICATES_EXPORT: 'certificates.export',
  CERTIFICATES_GENERATE: 'certificates.generate',

  // Bids
  BIDS_VIEW: 'bids.view',
  BIDS_EDIT: 'bids.edit',

  // Notes
  NOTES_CREATE: 'notes.create',
  NOTES_DELETE_OWN: 'notes.delete.own',
  NOTES_DELETE_OTHERS: 'notes.delete.others',

  // Cities
  CITIES_VIEW: 'cities.view',
  CITIES_EDIT: 'cities.edit',
  CITIES_EXPORT: 'cities.export',

  // Statistics
  STATISTICS_VIEW: 'statistics.view',

  // Reminders
  REMINDERS_VIEW: 'reminders.view',
  REMINDERS_EDIT: 'reminders.edit',

  // History
  HISTORY_VIEW: 'history.view',

  // Permissions Management
  PERMISSIONS_MANAGE: 'permissions.manage',

  // Inspector Workspace
  INSPECTOR_VIEW: 'inspector.view'
} as const;

export type PermissionKey =
  (typeof PERMISSION_KEYS)[keyof typeof PERMISSION_KEYS];

/**
 * All permission keys as a flat array (for validation and UI).
 */
export const ALL_PERMISSION_KEYS: PermissionKey[] =
  Object.values(PERMISSION_KEYS);

/**
 * Keys only the system owner holds (`User.isSystemOwner`, set by migration,
 * never through the API): AI configuration, AI costs and the system page,
 * i.e. everything under /app/super-admin. Roles, the super-admin bypass and
 * personal overrides never grant them, so the roles screen does not offer
 * them.
 */
export const OWNER_PERMISSION_KEYS: PermissionKey[] = [
  PERMISSION_KEYS.AI_SETTINGS_VIEW,
  PERMISSION_KEYS.AI_SETTINGS_EDIT,
  PERMISSION_KEYS.AI_USAGE_VIEW,
  PERMISSION_KEYS.SYSTEM_VIEW
];

// ─── Matrix Structure (for the new permissions management UI) ─────────────

export const ACTION_COLUMNS = [
  'view',
  'create',
  'edit',
  'delete',
  'export'
] as const;
export type ActionType = (typeof ACTION_COLUMNS)[number];

export const ACTION_LABELS: Record<ActionType, string> = {
  view: 'צפייה',
  create: 'יצירה',
  edit: 'עריכה',
  delete: 'מחיקה',
  export: 'ייצוא'
};

export interface PermissionMatrixRow {
  label: string;
  sensitive?: boolean;
  actions: Partial<Record<ActionType, PermissionKey>>;
}

export interface PermissionMatrixSection {
  key: string;
  label: string;
  rows: PermissionMatrixRow[];
}

export const PERMISSION_MATRIX: PermissionMatrixSection[] = [
  {
    key: 'orders',
    label: 'הזמנות',
    rows: [
      {
        label: 'הזמנות',
        actions: {
          view: PERMISSION_KEYS.ORDERS_VIEW,
          create: PERMISSION_KEYS.ORDERS_CREATE,
          edit: PERMISSION_KEYS.ORDERS_EDIT,
          delete: PERMISSION_KEYS.ORDERS_DELETE,
          export: PERMISSION_KEYS.ORDERS_EXPORT
        }
      },
      {
        label: 'עריכת מחיר',
        sensitive: true,
        actions: {
          view: PERMISSION_KEYS.ORDERS_PRICE_VIEW,
          edit: PERMISSION_KEYS.ORDERS_PRICE_EDIT
        }
      },
      {
        label: 'שינוי סטטוס',
        sensitive: true,
        actions: { edit: PERMISSION_KEYS.ORDERS_STATUS_EDIT }
      },
      {
        label: 'שינוי סטטוס מסגור',
        sensitive: true,
        actions: { edit: PERMISSION_KEYS.ORDERS_REOPEN_CLOSED }
      },
      {
        label: 'אישור הזמנה',
        actions: { edit: PERMISSION_KEYS.ORDERS_APPROVE }
      },
      {
        label: 'Icount',
        actions: { edit: PERMISSION_KEYS.ORDERS_ICOUNT }
      }
    ]
  },
  {
    key: 'subprojects',
    label: 'הסכמים',
    rows: [
      {
        label: 'הסכמים',
        actions: {
          view: PERMISSION_KEYS.SUBPROJECTS_VIEW,
          create: PERMISSION_KEYS.SUBPROJECTS_CREATE,
          edit: PERMISSION_KEYS.SUBPROJECTS_EDIT,
          delete: PERMISSION_KEYS.SUBPROJECTS_DELETE,
          export: PERMISSION_KEYS.SUBPROJECTS_EXPORT
        }
      },
      {
        label: 'מחירים',
        sensitive: true,
        actions: {
          view: PERMISSION_KEYS.SUBPROJECTS_PRICE_VIEW,
          edit: PERMISSION_KEYS.SUBPROJECTS_PRICE_EDIT
        }
      }
    ]
  },
  {
    key: 'projects',
    label: 'פרויקטים',
    rows: [
      {
        label: 'פרויקטים',
        actions: {
          view: PERMISSION_KEYS.PROJECTS_VIEW,
          create: PERMISSION_KEYS.PROJECTS_CREATE,
          edit: PERMISSION_KEYS.PROJECTS_EDIT,
          delete: PERMISSION_KEYS.PROJECTS_DELETE,
          export: PERMISSION_KEYS.PROJECTS_EXPORT
        }
      }
    ]
  },
  {
    key: 'inspections',
    label: `צ'קליסט`,
    rows: [
      {
        label: `צ'קליסט`,
        actions: {
          view: PERMISSION_KEYS.INSPECTIONS_VIEW,
          create: PERMISSION_KEYS.INSPECTIONS_CREATE,
          edit: PERMISSION_KEYS.INSPECTIONS_EDIT,
          // No delete action: the inspection delete route was removed. The key
          // stays registered so existing grants remain valid data.
          export: PERMISSION_KEYS.INSPECTIONS_EXPORT
        }
      },
      {
        label: `ניהול צ'קליסט`,
        actions: { edit: PERMISSION_KEYS.INSPECTIONS_MANAGE }
      },
      {
        label: `אישור צ'קליסט`,
        actions: { edit: PERMISSION_KEYS.INSPECTIONS_APPROVE }
      },
      {
        label: 'טיוטה',
        actions: { edit: PERMISSION_KEYS.INSPECTIONS_SKETCH }
      },
      {
        label: 'פתיחה מחדש',
        actions: { edit: PERMISSION_KEYS.INSPECTIONS_REOPEN }
      }
    ]
  },
  {
    key: 'templates',
    label: `תבניות צ'קליסט`,
    rows: [
      {
        label: 'תבניות',
        actions: {
          view: PERMISSION_KEYS.TEMPLATES_VIEW,
          create: PERMISSION_KEYS.TEMPLATES_CREATE,
          edit: PERMISSION_KEYS.TEMPLATES_EDIT,
          delete: PERMISSION_KEYS.TEMPLATES_DELETE
        }
      }
    ]
  },
  {
    key: 'inspections-bank',
    label: `בנק צ'קליסט`,
    rows: [
      {
        label: `בנק צ'קליסט`,
        actions: {
          view: PERMISSION_KEYS.INSPECTIONS_BANK_VIEW,
          edit: PERMISSION_KEYS.INSPECTIONS_BANK_EDIT
        }
      }
    ]
  },
  {
    key: 'companies',
    label: 'חברות',
    rows: [
      {
        label: 'ניהול חברות',
        actions: {
          view: PERMISSION_KEYS.COMPANIES_VIEW,
          create: PERMISSION_KEYS.COMPANIES_CREATE,
          edit: PERMISSION_KEYS.COMPANIES_EDIT,
          delete: PERMISSION_KEYS.COMPANIES_DELETE,
          export: PERMISSION_KEYS.COMPANIES_EXPORT
        }
      },
      {
        label: 'שינוי ח.פ / ע.מ.',
        actions: { edit: PERMISSION_KEYS.COMPANIES_ADMIN }
      },
      {
        label: 'שינוי סטטוס חברה',
        sensitive: true,
        actions: { edit: PERMISSION_KEYS.COMPANIES_STATUS_EDIT }
      }
    ]
  },
  {
    key: 'contacts',
    label: 'אנשי קשר',
    rows: [
      {
        label: 'אנשי קשר',
        actions: {
          view: PERMISSION_KEYS.CONTACTS_VIEW,
          create: PERMISSION_KEYS.CONTACTS_CREATE,
          edit: PERMISSION_KEYS.CONTACTS_EDIT,
          delete: PERMISSION_KEYS.CONTACTS_DELETE,
          export: PERMISSION_KEYS.CONTACTS_EXPORT
        }
      }
    ]
  },
  {
    key: 'contractors',
    label: 'קבלנים',
    rows: [
      {
        label: 'קבלנים',
        actions: {
          view: PERMISSION_KEYS.CONTRACTORS_VIEW,
          create: PERMISSION_KEYS.CONTRACTORS_CREATE,
          edit: PERMISSION_KEYS.CONTRACTORS_EDIT,
          delete: PERMISSION_KEYS.CONTRACTORS_DELETE,
          export: PERMISSION_KEYS.CONTRACTORS_EXPORT
        }
      }
    ]
  },
  {
    key: 'planners',
    label: 'מתכננים',
    rows: [
      {
        label: 'מתכננים',
        actions: {
          view: PERMISSION_KEYS.PLANNERS_VIEW,
          create: PERMISSION_KEYS.PLANNERS_CREATE,
          edit: PERMISSION_KEYS.PLANNERS_EDIT,
          delete: PERMISSION_KEYS.PLANNERS_DELETE,
          export: PERMISSION_KEYS.PLANNERS_EXPORT
        }
      }
    ]
  },
  {
    key: 'users',
    label: 'משתמשים',
    rows: [
      {
        label: 'משתמשים',
        actions: {
          view: PERMISSION_KEYS.USERS_VIEW,
          create: PERMISSION_KEYS.USERS_CREATE,
          edit: PERMISSION_KEYS.USERS_EDIT,
          delete: PERMISSION_KEYS.USERS_DELETE,
          export: PERMISSION_KEYS.USERS_EXPORT
        }
      },
      {
        label: 'ניהול משתמשים',
        actions: { edit: PERMISSION_KEYS.USERS_MANAGE }
      }
    ]
  },
  {
    key: 'certificates',
    label: 'תעודות',
    rows: [
      {
        label: 'תעודות',
        actions: {
          view: PERMISSION_KEYS.CERTIFICATES_VIEW,
          create: PERMISSION_KEYS.CERTIFICATES_GENERATE,
          edit: PERMISSION_KEYS.CERTIFICATES_EDIT,
          export: PERMISSION_KEYS.CERTIFICATES_EXPORT
        }
      },
      {
        label: 'העלאה',
        actions: { create: PERMISSION_KEYS.CERTIFICATES_UPLOAD }
      },
      {
        label: 'ניהול תעודות',
        actions: { edit: PERMISSION_KEYS.CERTIFICATES_ADMIN }
      }
    ]
  },
  {
    key: 'bids',
    label: 'הצעות מחיר',
    rows: [
      {
        label: 'הצעות מחיר',
        actions: {
          view: PERMISSION_KEYS.BIDS_VIEW,
          edit: PERMISSION_KEYS.BIDS_EDIT
        }
      }
    ]
  },
  {
    key: 'notes',
    label: 'הערות',
    rows: [
      {
        label: 'יצירת הערות',
        actions: { create: PERMISSION_KEYS.NOTES_CREATE }
      },
      {
        label: 'מחיקת הערות עצמיות',
        actions: { delete: PERMISSION_KEYS.NOTES_DELETE_OWN }
      },
      {
        label: 'מחיקת הערות של אחרים',
        sensitive: true,
        actions: { delete: PERMISSION_KEYS.NOTES_DELETE_OTHERS }
      }
    ]
  },
  {
    key: 'reports',
    label: 'דוחות',
    rows: [
      {
        label: 'לוח מחוונים',
        actions: { view: PERMISSION_KEYS.DASHBOARD_VIEW }
      },
      {
        label: 'סטטיסטיקות',
        actions: { view: PERMISSION_KEYS.STATISTICS_VIEW }
      },
      {
        label: 'היסטוריה',
        actions: { view: PERMISSION_KEYS.HISTORY_VIEW }
      }
    ]
  },
  {
    key: 'inspector',
    label: 'סקיצה',
    rows: [
      {
        label: 'סקיצה',
        actions: { view: PERMISSION_KEYS.INSPECTOR_VIEW }
      }
    ]
  },
  {
    key: 'system',
    label: 'מערכת',
    rows: [
      {
        label: 'ערים',
        actions: {
          view: PERMISSION_KEYS.CITIES_VIEW,
          edit: PERMISSION_KEYS.CITIES_EDIT,
          export: PERMISSION_KEYS.CITIES_EXPORT
        }
      },
      {
        label: 'תזכורות',
        actions: {
          view: PERMISSION_KEYS.REMINDERS_VIEW,
          edit: PERMISSION_KEYS.REMINDERS_EDIT
        }
      },
      {
        label: 'הרשאות',
        actions: { edit: PERMISSION_KEYS.PERMISSIONS_MANAGE }
      },
      {
        label: 'זמני בדיקות',
        actions: {
          view: PERMISSION_KEYS.SCHEDULING_SETTINGS_VIEW,
          edit: PERMISSION_KEYS.SCHEDULING_SETTINGS_EDIT
        }
      },
      {
        label: 'ניתוב מיילים (AI)',
        actions: {
          view: PERMISSION_KEYS.MAIL_TRIAGE_VIEW,
          edit: PERMISSION_KEYS.MAIL_TRIAGE_MANAGE
        }
      },
      {
        label: "חיבור צ'אט (Claude / ChatGPT)",
        sensitive: true,
        actions: {
          view: PERMISSION_KEYS.MCP_CONNECT
        }
      }
    ]
  }
];

/** Risk level metadata for roles (for the permissions management UI) */
export type RiskLevel = 'high' | 'medium' | 'low';

export const RISK_LEVEL_META: Record<
  RiskLevel,
  { label: string; color: string }
> = {
  high: { label: 'סיכון גבוה', color: '#ff4d4f' },
  medium: { label: 'סיכון בינוני', color: '#faad14' },
  low: { label: 'סיכון נמוך', color: '#52c41a' }
};

export const DEFAULT_ROLE_RISK_LEVELS: Record<string, RiskLevel> = {
  'super-admin': 'high',
  admin: 'medium',
  office: 'low',
  'senior-tester': 'medium',
  tester: 'low',
  guest: 'low'
};

// ─── Legacy groups (kept for backward compat) ────────────────────────────

/**
 * Permission keys grouped by module (for the admin UI).
 */
export const PERMISSION_GROUPS: {
  module: string;
  label: string;
  keys: { key: PermissionKey; label: string }[];
}[] = [
  {
    module: 'dashboard',
    label: 'לוח מחוונים',
    keys: [{ key: PERMISSION_KEYS.DASHBOARD_VIEW, label: 'צפייה' }]
  },
  {
    module: 'orders',
    label: 'הזמנות',
    keys: [
      { key: PERMISSION_KEYS.ORDERS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.ORDERS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.ORDERS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.ORDERS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.ORDERS_APPROVE, label: 'אישור' },
      { key: PERMISSION_KEYS.ORDERS_EXPORT, label: 'ייצוא' },
      { key: PERMISSION_KEYS.ORDERS_PRICE_VIEW, label: 'צפייה במחיר' },
      { key: PERMISSION_KEYS.ORDERS_PRICE_EDIT, label: 'עריכת מחיר' },
      { key: PERMISSION_KEYS.ORDERS_STATUS_EDIT, label: 'שינוי סטטוס' },
      {
        key: PERMISSION_KEYS.ORDERS_REOPEN_CLOSED,
        label: 'שינוי סטטוס מסגור'
      },
      { key: PERMISSION_KEYS.ORDERS_ICOUNT, label: 'Icount' }
    ]
  },
  {
    module: 'subprojects',
    label: 'הסכמים',
    keys: [
      { key: PERMISSION_KEYS.SUBPROJECTS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.SUBPROJECTS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.SUBPROJECTS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.SUBPROJECTS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.SUBPROJECTS_EXPORT, label: 'ייצוא' },
      { key: PERMISSION_KEYS.SUBPROJECTS_PRICE_VIEW, label: 'צפייה במחיר' },
      { key: PERMISSION_KEYS.SUBPROJECTS_PRICE_EDIT, label: 'עריכת מחיר' }
    ]
  },
  {
    module: 'projects',
    label: 'פרויקטים',
    keys: [
      { key: PERMISSION_KEYS.PROJECTS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.PROJECTS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.PROJECTS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.PROJECTS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.PROJECTS_EXPORT, label: 'ייצוא' }
    ]
  },
  {
    module: 'inspections',
    label: `צ'קליסט`,
    keys: [
      { key: PERMISSION_KEYS.INSPECTIONS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.INSPECTIONS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.INSPECTIONS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.INSPECTIONS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.INSPECTIONS_MANAGE, label: 'ניהול' },
      { key: PERMISSION_KEYS.INSPECTIONS_APPROVE, label: 'אישור' },
      { key: PERMISSION_KEYS.INSPECTIONS_EXPORT, label: 'ייצוא' },
      { key: PERMISSION_KEYS.INSPECTIONS_SKETCH, label: 'טיוטה' },
      { key: PERMISSION_KEYS.INSPECTIONS_REOPEN, label: 'פתיחה מחדש' }
    ]
  },
  {
    module: 'templates',
    label: `תבניות צ'קליסט`,
    keys: [
      { key: PERMISSION_KEYS.TEMPLATES_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.TEMPLATES_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.TEMPLATES_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.TEMPLATES_DELETE, label: 'מחיקה' }
    ]
  },
  {
    module: 'inspections-bank',
    label: `בנק צ'קליסט`,
    keys: [
      { key: PERMISSION_KEYS.INSPECTIONS_BANK_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.INSPECTIONS_BANK_EDIT, label: 'ניהול' }
    ]
  },
  {
    module: 'companies',
    label: 'חברות',
    keys: [
      { key: PERMISSION_KEYS.COMPANIES_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.COMPANIES_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.COMPANIES_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.COMPANIES_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.COMPANIES_EXPORT, label: 'ייצוא' },
      { key: PERMISSION_KEYS.COMPANIES_ADMIN, label: 'ניהול' },
      { key: PERMISSION_KEYS.COMPANIES_STATUS_EDIT, label: 'שינוי סטטוס' }
    ]
  },
  {
    module: 'contacts',
    label: 'אנשי קשר',
    keys: [
      { key: PERMISSION_KEYS.CONTACTS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.CONTACTS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.CONTACTS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.CONTACTS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.CONTACTS_EXPORT, label: 'ייצוא' }
    ]
  },
  {
    module: 'contractors',
    label: 'קבלנים',
    keys: [
      { key: PERMISSION_KEYS.CONTRACTORS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.CONTRACTORS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.CONTRACTORS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.CONTRACTORS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.CONTRACTORS_EXPORT, label: 'ייצוא' }
    ]
  },
  {
    module: 'planners',
    label: 'מתכננים',
    keys: [
      { key: PERMISSION_KEYS.PLANNERS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.PLANNERS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.PLANNERS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.PLANNERS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.PLANNERS_EXPORT, label: 'ייצוא' }
    ]
  },
  {
    module: 'users',
    label: 'משתמשים',
    keys: [
      { key: PERMISSION_KEYS.USERS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.USERS_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.USERS_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.USERS_DELETE, label: 'מחיקה' },
      { key: PERMISSION_KEYS.USERS_EXPORT, label: 'ייצוא' },
      { key: PERMISSION_KEYS.USERS_MANAGE, label: 'ניהול' }
    ]
  },
  {
    module: 'certificates',
    label: 'תעודות',
    keys: [
      { key: PERMISSION_KEYS.CERTIFICATES_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.CERTIFICATES_UPLOAD, label: 'העלאה' },
      { key: PERMISSION_KEYS.CERTIFICATES_EXPORT, label: 'ייצוא' },
      { key: PERMISSION_KEYS.CERTIFICATES_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.CERTIFICATES_ADMIN, label: 'ניהול' },
      { key: PERMISSION_KEYS.CERTIFICATES_GENERATE, label: 'יצירה' }
    ]
  },
  {
    module: 'bids',
    label: 'הצעות מחיר',
    keys: [
      { key: PERMISSION_KEYS.BIDS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.BIDS_EDIT, label: 'עריכה' }
    ]
  },
  {
    module: 'notes',
    label: 'הערות',
    keys: [
      { key: PERMISSION_KEYS.NOTES_CREATE, label: 'יצירה' },
      { key: PERMISSION_KEYS.NOTES_DELETE_OWN, label: 'מחיקת הערות עצמיות' },
      {
        key: PERMISSION_KEYS.NOTES_DELETE_OTHERS,
        label: 'מחיקת הערות של אחרים'
      }
    ]
  },
  {
    module: 'cities',
    label: 'ערים',
    keys: [
      { key: PERMISSION_KEYS.CITIES_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.CITIES_EDIT, label: 'עריכה' },
      { key: PERMISSION_KEYS.CITIES_EXPORT, label: 'ייצוא' }
    ]
  },
  {
    module: 'statistics',
    label: 'סטטיסטיקות',
    keys: [{ key: PERMISSION_KEYS.STATISTICS_VIEW, label: 'צפייה' }]
  },
  {
    module: 'reminders',
    label: 'תזכורות',
    keys: [
      { key: PERMISSION_KEYS.REMINDERS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.REMINDERS_EDIT, label: 'ניהול' }
    ]
  },
  {
    module: 'history',
    label: 'היסטוריה',
    keys: [{ key: PERMISSION_KEYS.HISTORY_VIEW, label: 'צפייה' }]
  },
  {
    module: 'permissions',
    label: 'הרשאות',
    keys: [{ key: PERMISSION_KEYS.PERMISSIONS_MANAGE, label: 'ניהול' }]
  },
  {
    module: 'inspector',
    label: 'סקיצה',
    keys: [{ key: PERMISSION_KEYS.INSPECTOR_VIEW, label: 'צפייה' }]
  },
  {
    module: 'scheduling-settings',
    label: 'זמני בדיקות',
    keys: [
      { key: PERMISSION_KEYS.SCHEDULING_SETTINGS_VIEW, label: 'צפייה' },
      { key: PERMISSION_KEYS.SCHEDULING_SETTINGS_EDIT, label: 'ניהול' }
    ]
  },
  {
    module: 'ai',
    label: 'בינה מלאכותית',
    keys: [
      { key: PERMISSION_KEYS.MAIL_TRIAGE_VIEW, label: 'צפייה בניתוב מיילים' },
      { key: PERMISSION_KEYS.MAIL_TRIAGE_MANAGE, label: 'ניהול ניתוב מיילים' },
      {
        key: PERMISSION_KEYS.MCP_CONNECT,
        label: "חיבור צ'אט חיצוני (Claude / ChatGPT) לנתוני LCL"
      }
    ]
  }
];

/**
 * Default permissions for migration: maps old role keys to their permission sets.
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<string, PermissionKey[]> = {
  // Owner keys belong to no role (see OWNER_PERMISSION_KEYS).
  'super-admin': ALL_PERMISSION_KEYS.filter(
    (k) => !OWNER_PERMISSION_KEYS.includes(k)
  ),
  admin: [
    PERMISSION_KEYS.DASHBOARD_VIEW,
    PERMISSION_KEYS.ORDERS_VIEW,
    PERMISSION_KEYS.ORDERS_CREATE,
    PERMISSION_KEYS.ORDERS_EDIT,
    PERMISSION_KEYS.ORDERS_DELETE,
    PERMISSION_KEYS.ORDERS_EXPORT,
    PERMISSION_KEYS.ORDERS_PRICE_VIEW,
    PERMISSION_KEYS.ORDERS_PRICE_EDIT,
    PERMISSION_KEYS.ORDERS_STATUS_EDIT,
    PERMISSION_KEYS.ORDERS_REOPEN_CLOSED,
    PERMISSION_KEYS.ORDERS_ICOUNT,
    PERMISSION_KEYS.SUBPROJECTS_VIEW,
    PERMISSION_KEYS.SUBPROJECTS_CREATE,
    PERMISSION_KEYS.SUBPROJECTS_EDIT,
    PERMISSION_KEYS.SUBPROJECTS_DELETE,
    PERMISSION_KEYS.SUBPROJECTS_EXPORT,
    PERMISSION_KEYS.SUBPROJECTS_PRICE_VIEW,
    PERMISSION_KEYS.SUBPROJECTS_PRICE_EDIT,
    PERMISSION_KEYS.PROJECTS_VIEW,
    PERMISSION_KEYS.PROJECTS_CREATE,
    PERMISSION_KEYS.PROJECTS_EDIT,
    PERMISSION_KEYS.PROJECTS_DELETE,
    PERMISSION_KEYS.PROJECTS_EXPORT,
    PERMISSION_KEYS.INSPECTIONS_VIEW,
    PERMISSION_KEYS.INSPECTIONS_CREATE,
    PERMISSION_KEYS.INSPECTIONS_EDIT,
    PERMISSION_KEYS.INSPECTIONS_DELETE,
    PERMISSION_KEYS.INSPECTIONS_MANAGE,
    PERMISSION_KEYS.INSPECTIONS_EXPORT,
    PERMISSION_KEYS.INSPECTIONS_SKETCH,
    PERMISSION_KEYS.INSPECTIONS_REOPEN,
    PERMISSION_KEYS.TEMPLATES_VIEW,
    PERMISSION_KEYS.TEMPLATES_CREATE,
    PERMISSION_KEYS.TEMPLATES_EDIT,
    PERMISSION_KEYS.TEMPLATES_DELETE,
    PERMISSION_KEYS.COMPANIES_VIEW,
    PERMISSION_KEYS.COMPANIES_CREATE,
    PERMISSION_KEYS.COMPANIES_EDIT,
    PERMISSION_KEYS.COMPANIES_DELETE,
    PERMISSION_KEYS.COMPANIES_EXPORT,
    PERMISSION_KEYS.COMPANIES_ADMIN,
    PERMISSION_KEYS.COMPANIES_STATUS_EDIT,
    PERMISSION_KEYS.CONTACTS_VIEW,
    PERMISSION_KEYS.CONTACTS_CREATE,
    PERMISSION_KEYS.CONTACTS_EDIT,
    PERMISSION_KEYS.CONTACTS_DELETE,
    PERMISSION_KEYS.CONTACTS_EXPORT,
    PERMISSION_KEYS.CONTRACTORS_VIEW,
    PERMISSION_KEYS.CONTRACTORS_CREATE,
    PERMISSION_KEYS.CONTRACTORS_EDIT,
    PERMISSION_KEYS.CONTRACTORS_DELETE,
    PERMISSION_KEYS.CONTRACTORS_EXPORT,
    PERMISSION_KEYS.PLANNERS_VIEW,
    PERMISSION_KEYS.PLANNERS_CREATE,
    PERMISSION_KEYS.PLANNERS_EDIT,
    PERMISSION_KEYS.PLANNERS_DELETE,
    PERMISSION_KEYS.PLANNERS_EXPORT,
    PERMISSION_KEYS.USERS_VIEW,
    PERMISSION_KEYS.USERS_CREATE,
    PERMISSION_KEYS.USERS_EDIT,
    PERMISSION_KEYS.USERS_DELETE,
    PERMISSION_KEYS.USERS_EXPORT,
    PERMISSION_KEYS.USERS_MANAGE,
    PERMISSION_KEYS.CERTIFICATES_VIEW,
    PERMISSION_KEYS.CERTIFICATES_EDIT,
    PERMISSION_KEYS.CERTIFICATES_ADMIN,
    PERMISSION_KEYS.CERTIFICATES_UPLOAD,
    PERMISSION_KEYS.CERTIFICATES_EXPORT,
    PERMISSION_KEYS.CERTIFICATES_GENERATE,
    PERMISSION_KEYS.BIDS_VIEW,
    PERMISSION_KEYS.BIDS_EDIT,
    PERMISSION_KEYS.NOTES_CREATE,
    PERMISSION_KEYS.NOTES_DELETE_OWN,
    PERMISSION_KEYS.NOTES_DELETE_OTHERS,
    PERMISSION_KEYS.CITIES_VIEW,
    PERMISSION_KEYS.CITIES_EDIT,
    PERMISSION_KEYS.CITIES_EXPORT,
    PERMISSION_KEYS.STATISTICS_VIEW,
    PERMISSION_KEYS.REMINDERS_VIEW,
    PERMISSION_KEYS.REMINDERS_EDIT,
    PERMISSION_KEYS.HISTORY_VIEW,
    PERMISSION_KEYS.SCHEDULING_SETTINGS_VIEW,
    PERMISSION_KEYS.SCHEDULING_SETTINGS_EDIT,
    PERMISSION_KEYS.MAIL_TRIAGE_VIEW,
    PERMISSION_KEYS.MAIL_TRIAGE_MANAGE
  ],
  office: [
    PERMISSION_KEYS.MAIL_TRIAGE_VIEW,
    PERMISSION_KEYS.MAIL_TRIAGE_MANAGE,
    PERMISSION_KEYS.DASHBOARD_VIEW,
    PERMISSION_KEYS.ORDERS_VIEW,
    PERMISSION_KEYS.ORDERS_CREATE,
    PERMISSION_KEYS.ORDERS_EDIT,
    PERMISSION_KEYS.ORDERS_PRICE_VIEW,
    PERMISSION_KEYS.ORDERS_PRICE_EDIT,
    PERMISSION_KEYS.ORDERS_STATUS_EDIT,
    PERMISSION_KEYS.SUBPROJECTS_VIEW,
    PERMISSION_KEYS.SUBPROJECTS_CREATE,
    PERMISSION_KEYS.SUBPROJECTS_EDIT,
    PERMISSION_KEYS.SUBPROJECTS_EXPORT,
    PERMISSION_KEYS.SUBPROJECTS_PRICE_VIEW,
    PERMISSION_KEYS.SUBPROJECTS_PRICE_EDIT,
    PERMISSION_KEYS.PROJECTS_VIEW,
    PERMISSION_KEYS.PROJECTS_CREATE,
    PERMISSION_KEYS.PROJECTS_EDIT,
    PERMISSION_KEYS.PROJECTS_EXPORT,
    PERMISSION_KEYS.INSPECTIONS_VIEW,
    PERMISSION_KEYS.INSPECTIONS_CREATE,
    PERMISSION_KEYS.INSPECTIONS_EDIT,
    PERMISSION_KEYS.INSPECTIONS_EXPORT,
    PERMISSION_KEYS.INSPECTIONS_SKETCH,
    PERMISSION_KEYS.TEMPLATES_VIEW,
    PERMISSION_KEYS.TEMPLATES_CREATE,
    PERMISSION_KEYS.TEMPLATES_EDIT,
    PERMISSION_KEYS.COMPANIES_VIEW,
    PERMISSION_KEYS.COMPANIES_CREATE,
    PERMISSION_KEYS.COMPANIES_EDIT,
    PERMISSION_KEYS.COMPANIES_EXPORT,
    PERMISSION_KEYS.COMPANIES_STATUS_EDIT,
    PERMISSION_KEYS.CONTACTS_VIEW,
    PERMISSION_KEYS.CONTACTS_CREATE,
    PERMISSION_KEYS.CONTACTS_EDIT,
    PERMISSION_KEYS.CONTACTS_EXPORT,
    PERMISSION_KEYS.CONTRACTORS_VIEW,
    PERMISSION_KEYS.CONTRACTORS_CREATE,
    PERMISSION_KEYS.CONTRACTORS_EDIT,
    PERMISSION_KEYS.CONTRACTORS_EXPORT,
    PERMISSION_KEYS.PLANNERS_VIEW,
    PERMISSION_KEYS.PLANNERS_CREATE,
    PERMISSION_KEYS.PLANNERS_EDIT,
    PERMISSION_KEYS.PLANNERS_EXPORT,
    PERMISSION_KEYS.USERS_VIEW,
    PERMISSION_KEYS.USERS_EXPORT,
    PERMISSION_KEYS.CERTIFICATES_VIEW,
    PERMISSION_KEYS.CERTIFICATES_EDIT,
    PERMISSION_KEYS.CERTIFICATES_UPLOAD,
    PERMISSION_KEYS.CERTIFICATES_GENERATE,
    PERMISSION_KEYS.BIDS_VIEW,
    PERMISSION_KEYS.BIDS_EDIT,
    PERMISSION_KEYS.NOTES_CREATE,
    PERMISSION_KEYS.NOTES_DELETE_OWN,
    PERMISSION_KEYS.CITIES_VIEW,
    PERMISSION_KEYS.CITIES_EDIT,
    PERMISSION_KEYS.CITIES_EXPORT,
    PERMISSION_KEYS.STATISTICS_VIEW,
    PERMISSION_KEYS.REMINDERS_VIEW,
    PERMISSION_KEYS.REMINDERS_EDIT
  ],
  'senior-tester': [
    PERMISSION_KEYS.DASHBOARD_VIEW,
    PERMISSION_KEYS.ORDERS_VIEW,
    PERMISSION_KEYS.ORDERS_CREATE,
    PERMISSION_KEYS.ORDERS_EDIT,
    PERMISSION_KEYS.ORDERS_PRICE_VIEW,
    PERMISSION_KEYS.ORDERS_STATUS_EDIT,
    PERMISSION_KEYS.SUBPROJECTS_VIEW,
    PERMISSION_KEYS.SUBPROJECTS_CREATE,
    PERMISSION_KEYS.SUBPROJECTS_EDIT,
    PERMISSION_KEYS.SUBPROJECTS_EXPORT,
    PERMISSION_KEYS.SUBPROJECTS_PRICE_VIEW,
    PERMISSION_KEYS.PROJECTS_VIEW,
    PERMISSION_KEYS.PROJECTS_CREATE,
    PERMISSION_KEYS.PROJECTS_EDIT,
    PERMISSION_KEYS.PROJECTS_EXPORT,
    PERMISSION_KEYS.INSPECTIONS_VIEW,
    PERMISSION_KEYS.INSPECTIONS_CREATE,
    PERMISSION_KEYS.INSPECTIONS_EDIT,
    PERMISSION_KEYS.INSPECTIONS_EXPORT,
    PERMISSION_KEYS.INSPECTIONS_SKETCH,
    PERMISSION_KEYS.TEMPLATES_VIEW,
    PERMISSION_KEYS.TEMPLATES_CREATE,
    PERMISSION_KEYS.TEMPLATES_EDIT,
    PERMISSION_KEYS.COMPANIES_VIEW,
    PERMISSION_KEYS.COMPANIES_CREATE,
    PERMISSION_KEYS.COMPANIES_EDIT,
    PERMISSION_KEYS.COMPANIES_EXPORT,
    PERMISSION_KEYS.CONTACTS_VIEW,
    PERMISSION_KEYS.CONTACTS_CREATE,
    PERMISSION_KEYS.CONTACTS_EDIT,
    PERMISSION_KEYS.CONTACTS_EXPORT,
    PERMISSION_KEYS.CONTRACTORS_VIEW,
    PERMISSION_KEYS.CONTRACTORS_CREATE,
    PERMISSION_KEYS.CONTRACTORS_EDIT,
    PERMISSION_KEYS.CONTRACTORS_EXPORT,
    PERMISSION_KEYS.PLANNERS_VIEW,
    PERMISSION_KEYS.PLANNERS_CREATE,
    PERMISSION_KEYS.PLANNERS_EDIT,
    PERMISSION_KEYS.PLANNERS_EXPORT,
    PERMISSION_KEYS.CERTIFICATES_VIEW,
    PERMISSION_KEYS.CERTIFICATES_EDIT,
    PERMISSION_KEYS.CERTIFICATES_UPLOAD,
    PERMISSION_KEYS.CERTIFICATES_GENERATE,
    PERMISSION_KEYS.BIDS_VIEW,
    PERMISSION_KEYS.BIDS_EDIT,
    PERMISSION_KEYS.NOTES_CREATE,
    PERMISSION_KEYS.NOTES_DELETE_OWN,
    PERMISSION_KEYS.NOTES_DELETE_OTHERS,
    PERMISSION_KEYS.CITIES_VIEW,
    PERMISSION_KEYS.CITIES_EDIT,
    PERMISSION_KEYS.CITIES_EXPORT,
    PERMISSION_KEYS.REMINDERS_VIEW,
    PERMISSION_KEYS.INSPECTOR_VIEW
  ],
  tester: [
    PERMISSION_KEYS.DASHBOARD_VIEW,
    PERMISSION_KEYS.INSPECTIONS_VIEW,
    PERMISSION_KEYS.INSPECTIONS_EDIT,
    PERMISSION_KEYS.INSPECTIONS_EXPORT,
    PERMISSION_KEYS.INSPECTIONS_SKETCH,
    PERMISSION_KEYS.CERTIFICATES_VIEW,
    PERMISSION_KEYS.NOTES_CREATE,
    PERMISSION_KEYS.NOTES_DELETE_OWN,
    PERMISSION_KEYS.INSPECTOR_VIEW
  ],
  guest: [PERMISSION_KEYS.DASHBOARD_VIEW]
};
